import { PbECommerceSettingsUseCaseEnum } from '@itsrever/rever-api'
import { createBrowserRouter, replace, RouterProvider } from 'react-router'
import {
  ItemSelection,
  NotFound,
  Error,
  ProcessSelectionPage,
  ReturnAddress,
  ReturnMethod,
  SuccessTracking,
  ChangeCompensationMethod
} from './pages'
import { RefundMethodsPageEntry } from './pages/portal/refund-methods/refund-method-entry.page'
import { LoginPage } from './pages/portal/login.page'
import { domains } from './utils/domains'
import { AppLayout } from './ui/layout/app-layout'
import { PortalLayout } from './ui/layout/portal-layout'
import { ConfirmationPage } from './pages/portal/confirmation.page'
import { CoverCancellationPage } from './pages/cover-cancellation/cover-cancellation.page'
import { CoverCancellationSuccessPage } from './pages/cover-cancellation/cover-cancellation-success.page'
import { store } from './redux/store'
import { TrackingPage } from './pages/tracking.page'
import { WithdrawalPage } from './pages/withdrawal/withdrawal.page'

const createRouter = (basePath: string) => {
  return createBrowserRouter([
    {
      Component: AppLayout,
      path: basePath,
      errorElement: <Error />,
      children: [
        {
          index: true,
          Component: LoginPage
        },
        {
          Component: PortalLayout,
          loader: ({ params }) => {
            const state = store.getState()
            const order = state.returnsApi.order.response.order
            const isReverLite =
              state.settingsApi.settings.response.use_case ===
              PbECommerceSettingsUseCaseEnum.Lite

            if (order || isReverLite) {
              return null
            }

            const isCustomDomain =
              domains[window.location.hostname] !== undefined

            throw replace(isCustomDomain ? '/' : `/${params.slug}`)
          },
          children: [
            {
              path: 'order',
              Component: ItemSelection,
              children: [
                {
                  path: 'item-options',
                  Component: null
                }
              ]
            },
            {
              path: 'return-address',
              Component: ReturnAddress
            },
            {
              path: 'refund-method',
              Component: RefundMethodsPageEntry
            },
            {
              path: 'return-method',
              Component: ReturnMethod
            },
            { path: 'confirm', Component: ConfirmationPage }
          ]
        },
        {
          path: 'success-tracking',
          loader: ({ request, params }) => {
            const searchParams = new URL(request.url).searchParams

            const email = searchParams.get('email')
            const processId = searchParams.get('processId')
            const orderNumber = searchParams.get('orderNumber')

            if (!email || !orderNumber || !processId) {
              const isCustomDomain =
                domains[window.location.hostname] !== undefined

              throw replace(isCustomDomain ? '/' : `/${params.slug}`)
            }

            return null
          },
          Component: SuccessTracking
        },
        { path: 'select', Component: ProcessSelectionPage },
        { path: 'withdrawal', Component: WithdrawalPage },
        {
          path: 'cover-cancellation',
          children: [
            { index: true, Component: CoverCancellationPage },
            { path: 'success', Component: CoverCancellationSuccessPage }
          ]
        },
        {
          path: 'change-of-compensation-opm-bt',
          loader: ({ request }) => {
            const searchParams = new URL(request.url).searchParams

            const email = searchParams.get('email')
            const processId = searchParams.get('process_id')

            if (!email || !processId) {
              throw new Response('Missing process_id or email', { status: 400 })
            }

            return null
          },
          Component: ChangeCompensationMethod
        },
        {
          path: 'tracking',
          loader: ({ request }) => {
            const searchParams = new URL(request.url).searchParams

            const email = searchParams.get('email')
            const processId = searchParams.get('processId')

            if (!email || !processId) {
              throw new Response('Missing processId or email', { status: 400 })
            }

            return null
          },
          children: [
            { index: true, Component: TrackingPage },
            {
              // for legacy upload-docs email links; upload now lives in tracking.
              path: 'documents',
              loader: ({ request, params }) => {
                const search = new URL(request.url).search
                const isCustomDomain =
                  domains[window.location.hostname] !== undefined
                const base = isCustomDomain ? '' : `/${params.slug}`
                throw replace(`${base}/tracking${search}`)
              }
            }
          ]
        },
        {
          path: 'error',
          Component: Error
        },
        {
          path: '*',
          Component: NotFound
        }
      ]
    },
    {
      path: 'error',
      Component: Error
    },
    {
      path: '*',
      Component: NotFound,
      errorElement: <Error />,
      HydrateFallback: () => null,
      loader: () => {
        if (!domains[window.location.hostname]) {
          return Response.redirect('https://itsrever.com/')
        }

        return null
      }
    }
  ])
}

export const App = () => {
  const isCustomDomain = domains[window.location.hostname] !== undefined
  const basePath = isCustomDomain ? '/' : '/:slug/'

  const router = createRouter(basePath)

  return <RouterProvider router={router} />
}
